import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import api from "../lib/api";
import { FadeUp } from "../components/Reveal";

export default function PaymentResult({ mode }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(mode === "success" ? "checking" : "cancelled");
  const attempts = useRef(0);

  useEffect(() => {
    if (mode !== "success" || !sessionId) return;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {}
      attempts.current += 1;
      if (attempts.current < 15) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [mode, sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-20">
      <FadeUp className="w-full max-w-md bg-white rounded-3xl border border-purple-100 p-10 text-center shadow-xl" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto text-purple-700 animate-spin" size={32} />
            <h1 className="font-serif text-3xl mt-6">Confirming your deposit…</h1>
            <p className="text-sm text-slate-500 mt-3">One moment while Stripe confirms the payment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <Check className="text-emerald-700" size={28} />
            </span>
            <h1 className="font-serif text-3xl mt-6" data-testid="payment-success-heading">You're locked in</h1>
            <p className="text-sm text-slate-500 mt-3 leading-relaxed">
              Deposit received — your session with Empress is confirmed and secured. The balance is due at your session.
            </p>
            <Link to="/portal/bookings" data-testid="payment-back-portal"
              className="inline-block mt-8 px-8 py-3.5 rounded-full bg-obsidian text-white text-sm hover:text-gold transition-colors">
              Back to my bookings
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto text-gold" size={32} />
            <h1 className="font-serif text-3xl mt-6">Almost there</h1>
            <p className="text-sm text-slate-500 mt-3 leading-relaxed">
              Stripe is still confirming your payment — your portal will update automatically within a minute.
            </p>
            <Link to="/portal/bookings" data-testid="payment-back-portal"
              className="inline-block mt-8 px-8 py-3.5 rounded-full bg-obsidian text-white text-sm hover:text-gold transition-colors">
              Back to my bookings
            </Link>
          </>
        )}
        {state === "cancelled" && (
          <>
            <span className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <X className="text-red-400" size={28} />
            </span>
            <h1 className="font-serif text-3xl mt-6" data-testid="payment-cancelled-heading">Payment cancelled</h1>
            <p className="text-sm text-slate-500 mt-3 leading-relaxed">
              No charge was made. Your booking stays approved — the deposit link is waiting in your portal whenever you're ready.
            </p>
            <Link to="/portal/bookings" data-testid="payment-back-portal"
              className="inline-block mt-8 px-8 py-3.5 rounded-full bg-obsidian text-white text-sm hover:text-gold transition-colors">
              Back to my bookings
            </Link>
          </>
        )}
      </FadeUp>
    </div>
  );
}
